import { useCallback, useState } from 'react';
import Konva from 'konva';
import jsPDF from 'jspdf';
import axios from 'axios';
import { useAppDispatch } from '../../../store/hooks';
import { updateBookData } from '../../../store/slices/bookSlice';
import { endpoints } from '../../../api/api';

function dataURLtoBlob(dataUrl: string): Blob {
  const [header, data] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)![1];
  const binary = atob(data);
  const arr = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

export interface CoverPageDimensions {
  /** Full cover page width in inches (back + spine + front + 2×bleed) */
  pageW: number;
  /** Full cover page height in inches (coverH + 2×bleed) */
  pageH: number;
  /** Bleed margin in inches on each side */
  bleed: number;
}

export function useExportAndSave(
  stageRef: React.RefObject<Konva.Stage | null>,
  pageDims?: CoverPageDimensions,
) {
  const dispatch = useAppDispatch();
  const [isSaving, setIsSaving]       = useState(false);
  const [saveError, setSaveError]     = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  /**
   * pixelRatio needed to hit targetDPI at the real print dimensions.
   * Canvas displays at ~61 px/inch for a 6×9 book; 300 DPI requires ≈5×.
   */
  const printPixelRatio = useCallback((targetDpi = 300) => {
    if (!stageRef.current || !pageDims) return 5;
    const displayPxPerInch = stageRef.current.height() / pageDims.pageH;
    return Math.ceil(targetDpi / displayPxPerInch);
  }, [stageRef, pageDims]);

  const exportAndSave = useCallback(async (bookId: string, onSuccess?: () => void) => {
    if (!stageRef.current) return;
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      // Hide transformer handles before capture so they don't appear in the export
      const transformers = stageRef.current.find('Transformer');
      transformers.forEach((t: Konva.Node) => t.hide());
      stageRef.current.batchDraw();

      // Export at 300 DPI relative to real print dimensions
      const dataUrl = stageRef.current.toDataURL({ pixelRatio: printPixelRatio(), mimeType: 'image/png' });

      // Restore transformer handles
      transformers.forEach((t: Konva.Node) => t.show());
      stageRef.current.batchDraw();

      // Embed in a PDF with 5mm white slug outside the image on all sides
      const pw    = pageDims?.pageW ?? stageRef.current.width();
      const ph    = pageDims?.pageH ?? stageRef.current.height();
      const bleed = pageDims?.bleed ?? 0.25;
      const SLUG  = 5 / 25.4; // 5mm white margin outside the image
      const totalW = pw + 2 * SLUG;
      const totalH = ph + 2 * SLUG;

      const doc = new jsPDF({
        orientation: totalW >= totalH ? 'landscape' : 'portrait',
        unit: 'in',
        format: [totalW, totalH],
      });

      // White background (slug area)
      doc.setFillColor('#FFFFFF');
      doc.rect(0, 0, totalW, totalH, 'F');

      // Cover image inset by slug on all sides (image already includes its own bleed)
      doc.addImage(dataUrl, 'PNG', SLUG, SLUG, pw, ph);

      // Crop marks — from page edge through slug, ending at the bleed boundary (cut line)
      const x0 = SLUG + bleed;        // left cut line
      const x1 = SLUG + pw - bleed;   // right cut line
      const y0 = SLUG + bleed;        // top cut line
      const y1 = SLUG + ph - bleed;   // bottom cut line
      doc.setDrawColor('#000000');
      doc.setLineWidth(0.75 / 72);
      // Top-left
      doc.line(0, y0, x0, y0);
      doc.line(x0, 0, x0, y0);
      // Top-right
      doc.line(x1, y0, totalW, y0);
      doc.line(x1, 0, x1, y0);
      // Bottom-left
      doc.line(0, y1, x0, y1);
      doc.line(x0, y1, x0, totalH);
      // Bottom-right
      doc.line(x1, y1, totalW, y1);
      doc.line(x1, y1, x1, totalH);

      const pdfBlob = doc.output('blob');

      const formData = new FormData();
      formData.append('file', pdfBlob, 'cover-design.pdf');
      formData.append('fileType', 'cover');
      formData.append('bookId', bookId);
      formData.append('coverSource', 'designer');
      const response = await axios.post(endpoints.uploadFile, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        dispatch(updateBookData({ coverImage: response.data.file.filename, coverImageSource: 'designer' }));
        if (onSuccess) {
          onSuccess();
        } else {
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 3000);
        }
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (err: any) {
      setSaveError(err.response?.data?.message || err.message || 'Failed to save cover');
    } finally {
      setIsSaving(false);
    }
  }, [stageRef, pageDims, printPixelRatio, dispatch]);

  /** Download the canvas as a print-ready PNG (300 DPI at actual print dimensions) */
  const downloadPNG = useCallback(() => {
    if (!stageRef.current) return;
    const dataUrl = stageRef.current.toDataURL({ pixelRatio: printPixelRatio(), mimeType: 'image/png' });
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = 'cover-design.png';
    a.click();
  }, [stageRef, printPixelRatio]);

  /**
   * Download the canvas as a PDF.
   * The PDF page size matches the real print dimensions (inches) so the
   * exported file is print-ready at the correct aspect ratio.
   */
  const downloadPDF = useCallback(() => {
    if (!stageRef.current) return;

    // Export at 300 DPI relative to the real print dimensions
    const dataUrl = stageRef.current.toDataURL({ pixelRatio: printPixelRatio(), mimeType: 'image/png' });

    // Use real inch dimensions if available; fall back to canvas pixel aspect ratio
    const pw = pageDims?.pageW ?? stageRef.current.width();
    const ph = pageDims?.pageH ?? stageRef.current.height();

    const doc = new jsPDF({
      orientation: pw >= ph ? 'landscape' : 'portrait',
      unit: 'in',
      format: [pw, ph],
    });

    // Fill the entire page with the cover image
    doc.addImage(dataUrl, 'PNG', 0, 0, pw, ph);
    doc.save('cover-design.pdf');
  }, [stageRef, pageDims, printPixelRatio]);

  return { exportAndSave, downloadPNG, downloadPDF, isSaving, saveError, saveSuccess };
}
